import React, { useCallback, useEffect, useState } from 'react';
import {
  TagEntities,
  Hotels,
  Rooms,
  RoomTypes,
  Tags as TagsApi,
} from '../../api/services/next';
import {
  EntityType as EntityTypes,
  MenuActions,
} from '../../components/shared/constants';
import { onError, onSuccess } from '../../components/shared/messages';
import { useTextResources } from '../../language';
import GridSearchTemplate from '../GridSearchTemplate';
import { RowEditModal } from './rowEditModal';
import uniqid from 'uniqid';
import { onUpdateGridHandler } from '../../components/GridPanel/utility';

const Tags = () => {
  const text = useTextResources();
  const [hotelsFromApi, setHotelsFromApi] = useState([]);
  const [gridData, setGridData] = useState([]);
  const [roomsFromApi, setRoomsFromApi] = useState([]);
  const [roomTypesFromApi, setRoomTypesFromApi] = useState([]);
  const [tagsFromApi, setTagsFromApi] = useState([]);
  const [deletedIds, setDeletedIds] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [roomsApi, roomTypesApi, hotelsApi, tagsApi] = await Promise.all([
          Rooms.GetRooms({ MaxResults: 1000 }),
          RoomTypes.GetRoomTypes(),
          Hotels.GetHotels({
            IsActive: true,
            IncludeOnlyHotelsWithUserPermissions: true,
          }),
          TagsApi.GetTags({
            IsSystem: false,
            ChainId:
              window.optima.user.HotelChainId || hotelsApi[0].HotelChainId,
          }),
        ]);
        setRoomsFromApi(roomsApi);
        setRoomTypesFromApi(roomTypesApi);
        setHotelsFromApi(hotelsApi);
        setTagsFromApi(tagsApi);
      } catch (error) {
        onError(error);
      }
    })();
  }, []);

  const save = useCallback(async () => {
    try {
      const deletedCleanIds = deletedIds.filter((id) => parseInt(id));
      //Ignore new entities that are not already saved in DB (starts with "newRow-")
      if (deletedCleanIds.length) {
        await TagEntities.DeleteTagEntities(deletedCleanIds);
        setDeletedIds([]);
      }
      const changes = gridData.filter((row) => row.dirty);
      if (changes.length) {
        const updatedData = await TagEntities.CreateOrUpdateGroupedTagEntities(changes);
        setGridData(updatedData.Value);
      }
      onSuccess();
    } catch (error) {
      onError(error);
    }
  }, [deletedIds, gridData]);

  useEffect(() => {
    if (window.optima?.commands) {
      window.optima.commands.unsubscribe(MenuActions.save);
      window.optima.commands.subscribe(MenuActions.save, save);
    }
  }, [save]); 

  const onAddTag = (tag) => {
    setTagsFromApi((prev) => [...prev, tag]);
  };

  const findSimilarItemExist = (data, row) =>
    data.find(
      (d) => d.EntityId == row.EntityId && d.EntityTypeId == row.EntityTypeId
    );

  const getTagIdsToAdd = (duplicatedRow, addedRow) => {
    const existingTagIds = Object.values(duplicatedRow.Tags);
    return Object.values(addedRow.Tags).filter(
      (id) => !existingTagIds.includes(id)
    );
  };

  const doesValueExistInObj = (object, value) =>
    Object.keys(object).some((key) => object[key] === value);

  const onGridUpdate = (added, changed, deleted) => {
    let updated = onUpdateGridHandler(gridData,added,changed,deleted);

    if (added) {
      const [addedRow] = added; //Always receives as an array of one element
      const duplicatedRow = findSimilarItemExist(updated, addedRow);
      const additionalProps = {
        dirty: true,
        ChainId:
          window.optima.user.HotelChainId || hotelsFromApi[0].HotelChainId,
      };

      if (duplicatedRow) {
        //User added a row that corresponds with another row's entityTypeId and entityId - merge both rows
        const tagIdsToAdd = getTagIdsToAdd(duplicatedRow, addedRow);

        const tagsToAdd = tagIdsToAdd.reduce((acc, id) => {
          acc[uniqid('newRow-')] = id;
          return acc;
        }, {});

        duplicatedRow.Tags = { ...duplicatedRow.Tags, ...tagsToAdd };

        //if there was a duplicated altered row, use it and add additional props
        updated = updated.map((u) =>
          u === duplicatedRow
            ? { ...duplicatedRow, ...additionalProps }
            : { ...u }
        );
      } else {
        updated.push({ ...addedRow, ...additionalProps });
      }
    }

    /*Received as a nested object {key: {changes}} even if changes (in that case, undefined)
    were not made, and thus being checked this way */
    const isChanged = Object.values(changed || [])[0];
    if (isChanged) {
      const [rowKey] = Object.keys(changed);
      const editedRow = updated[rowKey];
      const changes = Object.entries(changed[rowKey]);

      changes.forEach(([property, change]) => {
        if (property !== ColumnsEnum.Tags) {
          editedRow[property] = change;
        } else {
          editedRow.Tags = change;
        }
      });

      const duplications = updated.filter(
        (u) =>
          u.EntityId == editedRow.EntityId &&
          u.EntityTypeId == editedRow.EntityTypeId
      );

      if (duplications.length == 2) {
        // Produce combined tags without duplications and delete duplicated rows
        const [{ Tags: tags1 }, { Tags: tags2 }] = duplications;

        editedRow.Tags = Object.entries({ ...tags1, ...tags2 }).reduce(
          (acc, [rowId, tagId]) => {
            if (doesValueExistInObj(acc, tagId)) {
              //Makes sure no duplicates are allowed
              setDeletedIds((prev) => [...prev, rowId]);
              return acc;
            }
            acc[rowId] = tagId;
            return acc;
          },
          {}
        );
      }
      editedRow.dirty = true;
      updated = updated.filter((u) => u === duplications.find((d) => d.dirty)); //filter out the duplication that is no longer relevant following the merge
    }

    if (deleted) {
      setDeletedIds((prev) => [...prev, ...Object.keys(gridData[deleted].Tags)]);
      updated = updated.filter((_, i) => !deleted.includes(i));
    }

    setGridData(updated);
  };

  const entityTypeFormatter = ({ row }) => {
    return (
      Object.keys(EntityTypes).find(
        (type) => EntityTypes[type] === row.EntityTypeId
      ) || ''
    );
  };

  const hotelFormatter = ({ row }) => {
    return hotelsFromApi?.find((h) => h.Id === row.HotelId)?.Name || '';
  };

  const entityFormatter = ({ row: { EntityTypeId, EntityId } }) => {
    const entity = {
      [EntityTypes.Room]: roomsFromApi.find((v) => v.Id === EntityId),
      [EntityTypes.RoomType]: roomTypesFromApi.find((v) => v.Id === EntityId),
    }[EntityTypeId];
    return entity?.Name || '';
  };

  const tagsFormatter = ({ row }) => {
    if (row.Tags) {
      return row.Tags
        ? Object.values(row.Tags)
            ?.map((id) => tagsFromApi.find((v) => v.Id === id)?.Tag1)
            .join(', ')
        : '';
    }
  };

  const gridOptions = {
    search: {
      title: `${text('Tags')}`,
      api: TagEntities.GetGroupedTagEntities,
      spec: { EntityTypeIds: [6, 12] },
      searchOnLoad: true,
    },
    grid: {
      columns: [
        {
          name: ColumnsEnum.HotelId,
          title: `${text('Hotel')}`,
          formatter: hotelFormatter,
        },
        {
          name: ColumnsEnum.EntityTypeId,
          title: `${text('EntityType')}`,
          formatter: entityTypeFormatter,
        },
        {
          name: ColumnsEnum.EntityId,
          title: `${text('Entity')}`,
          formatter: entityFormatter,
        },
        {
          name: ColumnsEnum.Tags,
          title: ColumnsEnum.Tags,
          formatter: tagsFormatter,
        },
        {
          name: ColumnsEnum.RowIds,
        },
        {
          name: ColumnsEnum.ChainId,
        },
      ],
      scrollbarX: true,
      customOptions:{
        hidden:{
          hiddenColumns: [ {columnName:  ColumnsEnum.ChainId,togglingEnabled: false},{columnName:  ColumnsEnum.RowIds,togglingEnabled: false}]
        }
      },
      editable: {
        allowAdd: true,
        allowDelete: true,
        allowEdit: true,
        modalEditor: {
          modal: RowEditModal,
          props: {
            hotelsFromApi,
            onAddTag,
            roomTypesFromApi,
            tagsFromApi,
            onDeleteRow: setDeletedIds,
          },
        },
      },
    },
  };
  return (
    <div style={{ flexDirection: 'column' }}>
      <GridSearchTemplate
        options={gridOptions}
        onGridUpdate={onGridUpdate}
        onSearchComplete={setGridData}
        data={gridData}
      />
    </div>
  );
};

const ColumnsEnum = {
  HotelId: 'HotelId',
  EntityTypeId: 'EntityTypeId',
  RowIds: 'RowIds',
  EntityId: 'EntityId',
  Tags: 'Tags',
  ChainId: 'ChainId',
};

export default Tags;
